import React, { useState } from 'react';
import { Rss, Search, Filter, RefreshCw, Download, ExternalLink, Power, Settings } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';

const ThreatFeedsList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Sample data - in production, this would come from an API
  const feeds = Array.from({ length: 50 }, (_, i) => ({
    id: `feed-${i + 1}`,
    name: `Threat Feed ${i + 1}`,
    type: i % 3 === 0 ? 'Malware' : i % 3 === 1 ? 'Phishing' : 'Botnet',
    provider: i % 2 === 0 ? 'Internal' : 'External',
    status: i % 4 === 0 ? 'active' : i % 4 === 1 ? 'inactive' : i % 4 === 2 ? 'error' : 'maintenance',
    lastUpdate: new Date(Date.now() - Math.random() * 10000000000).toISOString(),
    indicators: Math.floor(Math.random() * 10000),
    accuracy: Math.floor(Math.random() * 20 + 80)
  }));

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedFeeds = feeds.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Rss className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Threat Feeds</h1>
            <p className="text-sm text-gray-400">Manage and monitor threat intelligence feeds</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
          <input
            type="text"
            placeholder="Search threat feeds..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
        </div>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filter
        </Button>
      </div>

      {/* Feeds Table */}
      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Name</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Type</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Provider</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Indicators</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Accuracy</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Last Update</th>
              {/* <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th> */}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {displayedFeeds.map((feed) => (
              <tr key={feed.id} className="group hover:bg-gray-800/30">
                <td className="px-6 py-4">
                  <span className="text-sm text-white">{feed.name}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-300">{feed.type}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-300">{feed.provider}</span>
                </td>
                <td className="px-6 py-4">
                  <Badge variant={feed.status as any}>{feed.status}</Badge>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-300">{feed.indicators.toLocaleString()}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          feed.accuracy >= 90 ? 'bg-emerald-500' :
                          feed.accuracy >= 80 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${feed.accuracy}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-300">{feed.accuracy}%</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-300">
                    {new Date(feed.lastUpdate).toLocaleString()}
                  </span>
                </td>
                {/* <td className="px-6 py-4">
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="outline" size="sm">
                      <Power className="h-4 w-4 mr-2" />
                      Toggle
                    </Button>
                    <Button variant="outline" size="sm">
                      <Settings className="h-4 w-4 mr-2" />
                      Configure
                    </Button>
                    <Button variant="outline" size="sm">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View
                    </Button>
                  </div>
                </td> */}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Showing {startIndex + 1} to {Math.min(endIndex, feeds.length)} of {feeds.length} feeds
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="px-3 py-1 rounded-lg bg-blue-500/10 text-blue-500">
              {currentPage}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={endIndex >= feeds.length}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreatFeedsList;